import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { router, Link } from '@inertiajs/react';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft } from 'lucide-react';

const formSchema = z.object({
  name: z.string().min(1, 'University name is required'),
  details: z.string().optional(),
  requirements: z.string().optional(),
  code: z.string().optional(),
  link: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  published: z.boolean(),
});

type FormData = z.infer<typeof formSchema>;

type FeeWaiverPartner = {
  id: number | null;
  name: string;
  details: string | null;
  requirements: string | null;
  code: string | null;
  link: string | null;
  published: boolean;
  errors?: string[];
};

type Props = {
  fee_waiver_partner: FeeWaiverPartner;
};

export default function FeeWaiverPartnersForm({ fee_waiver_partner }: Props) {
  const isEditing = fee_waiver_partner.id !== null;
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: fee_waiver_partner.name || '',
      details: fee_waiver_partner.details || '',
      requirements: fee_waiver_partner.requirements || '',
      code: fee_waiver_partner.code || '',
      link: fee_waiver_partner.link || '',
      published: fee_waiver_partner.published ?? true,
    },
  });

  const onSubmit = (data: FormData) => {
    if (isEditing) {
      router.patch(`/admin/fee_waiver_partners/${fee_waiver_partner.id}`, {
        fee_waiver_partner: data,
      });
    } else {
      router.post('/admin/fee_waiver_partners', {
        fee_waiver_partner: data,
      });
    }
  };

  return (
    <AdminLayout 
      title={isEditing ? 'Edit Fee Waiver Partner' : 'New Fee Waiver Partner'}
      breadcrumbs={[
        { label: 'Fee Waiver Partners', href: '/admin/fee_waiver_partners' },
      ]}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            href="/admin/fee_waiver_partners"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Fee Waiver Partners
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-gray-900">
            {isEditing ? 'Edit Fee Waiver Partner' : 'Add New Fee Waiver Partner'}
          </h1>
        </div>

        {/* Error messages */}
        {fee_waiver_partner.errors && fee_waiver_partner.errors.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm font-medium text-red-800">Please fix the following errors:</p>
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
              {fee_waiver_partner.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Form */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 bg-white shadow rounded-lg p-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>University Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Cornell University" {...field} />
                  </FormControl>
                  <FormDescription>
                    The full name of the university or institution
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="details"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Program Details</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Graduate School" {...field} />
                  </FormControl>
                  <FormDescription>
                    Specific school or program offering the waiver (optional)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Waiver Code</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., SHORT21" {...field} />
                  </FormControl>
                  <FormDescription>
                    Fee waiver code if applicable (optional)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="requirements"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Requirements</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="e.g., Requires a signed letter from the applicant's program director..."
                      className="min-h-[100px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>
                    Special requirements or instructions for this waiver (optional)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="link"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Partner Website</FormLabel>
                  <FormControl>
                    <Input 
                      type="url" 
                      placeholder="https://university.edu/fee-waivers" 
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>
                    Link to the university's fee waiver information page (optional)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-center justify-between pt-6 border-t">
              <FormField
                control={form.control}
                name="published"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Published</FormLabel>
                      <FormDescription>
                        Make this partner visible on the public fee waivers page
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => router.visit('/admin/fee_waiver_partners')}
              >
                Cancel
              </Button>
              <Button type="submit">
                {isEditing ? 'Update Partner' : 'Create Partner'}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </AdminLayout>
  );
}